import React, { useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { addPadAttachment, createPadEntry, getPadSettings, removePadAttachment, updatePadEntry, uploadPadAttachmentFile } from "./api";
import PadAttachments from "./PadAttachments";
import {
  PAD_CATEGORIES,
  ALTERNATE_METHODS,
  STOP_REASONS,
  categoryMeta,
  totalRentFromItem,
  rentBreakdownFromItem,
  moveOutDateFromItem,
  leaseStartDateFromItem,
  formatMonthLabel,
  money,
  monthOf,
  round2,
  todayYmd,
  dayOrdinal,
  DEFAULT_DELAY_APPROVAL_DAY,
} from "./padInfoTypes";

// Visual tokens, shared with the addendum modal so the two sections read as one product.
const C = {
  primary: "#0C447C",
  primaryMd: "#1A6FB5",
  border: "#E4E9F2",
  inputBorder: "#D6DEEA",
  muted: "#64748B",
  text: "#0F1F35",
  soft: "#F7F9FC",
};
const T = { title: 16, body: 13, label: 12, hint: 11 };

// The confirmation card shows user-typed values (names, reasons, notes) inside Swal HTML.
const escapeText = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// The delay charge as typed → dollars with cents, or null when it is not a number yet.
// Mirrors normalizeEntryPayload on the server; both round to cents.
const parseCharge = (v) => {
  const raw = String(v ?? "").replace(/[^0-9.]/g, "");
  if (!raw) return null;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
};

const styles = {
  body: { padding: "16px 18px 18px", maxHeight: "72vh", overflowY: "auto", background: "#fff" },
  label: { fontSize: T.label, fontWeight: 600, color: C.text, marginBottom: 5, display: "block", lineHeight: 1.3 },
  fieldBox: {
    display: "flex", alignItems: "center", gap: 8, height: 38,
    border: `1px solid ${C.inputBorder}`, borderRadius: 9, padding: "0 10px", background: "#fff",
  },
  bareInput: {
    border: "none", outline: "none", background: "transparent", flex: 1, width: "100%",
    fontSize: T.body, color: C.text, height: "100%", fontFamily: "inherit", minWidth: 0,
  },
  hint: { fontSize: T.hint, fontWeight: 500, color: C.muted, lineHeight: 1.4 },
};

// A label over a control. No helper sentences: the label says what the field is, the
// validation message in the footer says what is missing. `labelExtra` sits in small
// just right of the label text (the N/A toggle lives there) — as a SIBLING of the
// label, never inside it: a label wrapping a button becomes that button's name and
// forwards every click on the label text to it.
const Field = ({ label, labelExtra, children, style }) => (
  <div style={style}>
    {labelExtra ? (
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
        <label style={{ ...styles.label, marginBottom: 0 }}>{label}</label>
        {labelExtra}
      </div>
    ) : (
      <label style={styles.label}>{label}</label>
    )}
    {children}
  </div>
);

// The small N/A pill beside a resume-date label: an explicit "no date on purpose",
// as opposed to a date nobody has picked yet.
const NaToggle = ({ on, onClick, disabled }) => (
  <button type="button" className={`pim-na${on ? " on" : ""}`} aria-pressed={on} onClick={onClick} disabled={disabled}>
    N/A
  </button>
);

const Box = ({ children, className = "", style, title }) => (
  <div className={`pim-focusable ${className}`} style={{ ...styles.fieldBox, ...style }} title={title || undefined}>
    {children}
  </div>
);

/**
 * The category picker — a select, not a wall of cards (owner, 2026-08-31): one compact
 * control, with the picked category's one-line description underneath so the meaning
 * still travels. Empty until a choice is made; archived mode offers only a PAD Stop.
 */
const CategoryPicker = ({ value, onPick, disabled, labelledBy, categories = PAD_CATEGORIES }) => {
  const picked = categories.find((c) => c.value === value) || null;
  return (
    <div>
      <Box>
        {picked ? (
          <span style={{ width: 22, height: 22, borderRadius: 6, background: picked.bg, color: picked.color, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
            <FeatherIcon icon={picked.icon} size={13} />
          </span>
        ) : (
          <FeatherIcon icon="list" size={14} color={C.muted} />
        )}
        <select
          style={{ ...styles.bareInput, cursor: "pointer" }}
          aria-labelledby={labelledBy}
          disabled={disabled}
          value={value}
          onChange={(e) => e.target.value && onPick(e.target.value)}
        >
          <option value="" disabled>
            Select the kind of PAD update…
          </option>
          {categories.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </Box>
      {picked && <div style={{ ...styles.hint, marginTop: 6 }}>{picked.blurb}</div>}
    </div>
  );
};

// `na` is form state only, never sent: it marks "no resume date on purpose" (the pull
// stays delayed / stopped until further notice) and is stored as an empty resume_date.
const emptyForm = (tenantNames = "") => ({
  tenant_names: tenantNames,
  category: "",
  effective_date: "",
  // `incentives` is the move-in deal's repeater: {amount, date} rows, one per discounted month.
  new_pad: { lease_start_date: "", reason: "", incentives: [] },
  transfer: { reason: "" },
  amount_change: { one_time_amount: "", one_time_reason: "" },
  delay: { month: "", resume_date: "", charge: "", na: false, approved_by: "", late_fees_collected: false },
  // A split starts as the smallest schedule that is a split at all: two empty pulls.
  // `na` mirrors the delay's: "no resume date on purpose" — the split repeats until replaced.
  split: { pulls: [{ amount: "", date: "" }, { amount: "", date: "" }], approved_by: "", resume_date: "", na: false, charge: "" },
  temp_stop: { alternate_method: "", resume_date: "", na: false },
  stop: { reason: "", move_out_date: "" },
  notes: "",
});

const fromExisting = (e) => ({
  tenant_names: e.tenant_names || "",
  category: e.category || "",
  effective_date: e.effective_date || "",
  new_pad: {
    lease_start_date: "",
    reason: "",
    ...(e.new_pad || {}),
    incentives: (e.new_pad?.incentives || []).map((i) => ({ amount: i.amount === 0 || i.amount ? String(i.amount) : "", date: i.date || "" })),
  },
  transfer: { reason: "", ...(e.transfer || {}) },
  amount_change: {
    one_time_amount: e.amount_change?.one_time_amount ? String(e.amount_change.one_time_amount) : "",
    one_time_reason: e.amount_change?.one_time_reason || "",
  },
  // A saved entry with no resume date was recorded as open-ended — that IS the N/A state.
  delay: {
    month: "",
    resume_date: "",
    approved_by: "",
    ...(e.delay || {}),
    charge: e.delay?.charge === 0 || e.delay?.charge ? String(e.delay.charge) : "",
    na: e.category === "DELAY" && !(e.delay && e.delay.resume_date),
    late_fees_collected: !!e.delay?.late_fees_collected,
  },
  split: {
    pulls:
      e.split?.pulls?.length
        ? e.split.pulls.map((p) => ({ amount: p.amount === 0 || p.amount ? String(p.amount) : "", date: p.date || "" }))
        : [{ amount: "", date: "" }, { amount: "", date: "" }],
    approved_by: e.split?.approved_by || "",
    resume_date: e.split?.resume_date || "",
    // A saved split with no resume date was recorded as open-ended — that IS the N/A state.
    na: e.category === "SPLIT" && !(e.split && e.split.resume_date),
    charge: e.split?.charge ? String(e.split.charge) : "",
  },
  temp_stop: { alternate_method: "", resume_date: "", ...(e.temp_stop || {}), na: e.category === "TEMP_STOP" && !(e.temp_stop && e.temp_stop.resume_date) },
  stop: { reason: "", move_out_date: "", ...(e.stop || {}) },
  notes: e.notes || "",
});

/**
 * Create / edit one PAD Info entry. One surface, sized for small laptop screens: the category
 * list with the attachments panel beside it, the category's fields below, then notes. No money
 * and no banking details are captured here: the rent is read back from the unit's mastersheet
 * row, and account information never enters this system.
 *
 * The resident is NOT a field. It already sits in the header, and it is never editable. The
 * names still travel in the payload (a New PAD takes the incoming lease's tenants).
 */
const PadInfoModal = ({ isOpen, toggle, itemId, item, unitNo, buildingName, propertyName, tenantNames, incoming, existing, onSaved, archivedItemId }) => {
  // Archived-tenancy mode (opened from the Archived Mastersheet): the resident is gone,
  // so the only recordable update is a PAD Stop — pre-picked, no other categories shown.
  const archivedMode = !!archivedItemId;
  const [form, setForm] = useState(() => (existing ? fromExisting(existing) : emptyForm(tenantNames)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Attachments are staged, not uploaded on pick: a brand-new entry has nothing to hang a file
  // on yet, and Cancel must not leave an orphaned object in the bucket. Everything is applied
  // in one go by save().
  const [stagedFiles, setStagedFiles] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const [savingStep, setSavingStep] = useState("");
  // If the entry saves but an upload then fails, the entry still exists. Remembering its id
  // means pressing Save again edits that entry rather than creating a second one.
  const [createdId, setCreatedId] = useState(null);
  // The company's cutoff: a delayed pull past this day-of-month needs an approver's name.
  // The default stands in when the fetch fails — the server still enforces the real value.
  const [delayApprovalDay, setDelayApprovalDay] = useState(DEFAULT_DELAY_APPROVAL_DAY);
  useEffect(() => {
    if (!isOpen) return undefined;
    let active = true;
    getPadSettings()
      .then((resp) => {
        const day = Number(resp?.data?.data?.delay_approval_day);
        if (active && Number.isFinite(day) && day >= 1 && day <= 31) setDelayApprovalDay(day);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const base = existing ? fromExisting(existing) : emptyForm(tenantNames);
      // An archived tenancy records only a PAD Stop: pre-pick it and date it from the
      // tenancy's lease end, exactly what pickCategory would have done.
      if (archivedMode && !existing) {
        base.category = "STOP";
        const moveOut = moveOutDateFromItem(item);
        if (moveOut) base.effective_date = moveOut;
      }
      setForm(base);
      setError("");
      setStagedFiles([]);
      setRemovedAttachmentIds([]);
      setSavingStep("");
      setCreatedId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existing?._id]);

  const meta = form.category ? categoryMeta(form.category) : null;
  const fields = meta ? meta.fields : [];
  // Rent is never entered or stored here: it is always the mastersheet's number.
  const totalRent = useMemo(() => totalRentFromItem(item), [item]);
  const rentBreakdown = useMemo(() => rentBreakdownFromItem(item), [item]);
  // A PAD Stop pre-fills its move-out date from the lease end column, but stays editable.
  const systemMoveOut = useMemo(() => moveOutDateFromItem(item), [item]);
  // A New PAD's first pull is the lease start: the incoming signed lease's date when there is
  // one, else the mastersheet row's lease start column. Moving the pull off it needs a reason.
  const systemLeaseStart = useMemo(
    () => (incoming && incoming.start_date) || leaseStartDateFromItem(item) || "",
    [incoming, item]
  );
  const firstPullEdited =
    form.category === "NEW" && !!systemLeaseStart && !!form.effective_date && form.effective_date !== systemLeaseStart;
  // The delay surcharge as currently typed — folded into the shown total so what the form
  // displays is what the delayed months are actually charged.
  const delayCharge = form.category === "DELAY" ? parseCharge(form.delay.charge) || 0 : 0;
  // The one-time extra a New Rent Amount collects in its effective month — folded into the
  // shown total so the form displays what that month is actually charged.
  const oneTimeCharge = form.category === "AMOUNT_CHANGE" ? parseCharge(form.amount_change.one_time_amount) || 0 : 0;
  // Past the company's cutoff day, a delayed pull needs the name of who approved it.
  const delayNeedsApproval =
    form.category === "DELAY" && !!form.effective_date && Number(form.effective_date.slice(8, 10)) > delayApprovalDay;
  // The split schedule as typed: its running total, the date it starts on (its earliest
  // pull), the month's target (derived rent + the manager-typed extra charges), and whether
  // the pulls cover that target exactly. Saving is blocked until they do — a split is a way
  // to pay the month's total, never a different amount.
  const splitPulls = form.category === "SPLIT" ? form.split.pulls : [];
  const splitTotal = Math.round(splitPulls.reduce((s, p) => s + (parseCharge(p.amount) || 0), 0) * 100) / 100;
  const splitFirstDate = splitPulls.map((p) => p.date).filter(Boolean).sort()[0] || "";
  const splitCharge = form.category === "SPLIT" ? parseCharge(form.split.charge) || 0 : 0;
  const splitTarget = totalRent === null ? null : Math.round((totalRent + splitCharge) * 100) / 100;
  const splitMatchesRent = splitTarget !== null && Math.abs(splitTotal - splitTarget) <= 0.005;

  // No date may point into the past on a NEW entry — every field, every category (owner's
  // direction, 2026-08-28). Two exemptions, both deliberate: editing (an ongoing entry's
  // own dates are history) and archived-tenancy stops (recorded after the fact by design).
  const today = todayYmd();
  const blockPast = !existing?._id && !archivedMode;
  // A resume can never precede the pause, and on a new entry never precede today either.
  const resumeMin = blockPast ? (form.effective_date > today ? form.effective_date : today) : undefined;
  // Same floor for a split's resume — its start is the first pull.
  const splitResumeMin = blockPast ? (splitFirstDate > today ? splitFirstDate : today) : undefined;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setSub = (key, patch) => setForm((f) => ({ ...f, [key]: { ...f[key], ...patch } }));
  const setPull = (i, patch) => setForm((f) => ({ ...f, split: { ...f.split, pulls: f.split.pulls.map((p, idx) => (idx === i ? { ...p, ...patch } : p)) } }));
  const addPull = () => setForm((f) => ({ ...f, split: { ...f.split, pulls: [...f.split.pulls, { amount: "", date: "" }] } }));
  const removePull = (i) => setForm((f) => ({ ...f, split: { ...f.split, pulls: f.split.pulls.filter((_, idx) => idx !== i) } }));
  const setIncentive = (i, patch) => setForm((f) => ({ ...f, new_pad: { ...f.new_pad, incentives: f.new_pad.incentives.map((p, idx) => (idx === i ? { ...p, ...patch } : p)) } }));
  const addIncentive = () => setForm((f) => ({ ...f, new_pad: { ...f.new_pad, incentives: [...f.new_pad.incentives, { amount: "", date: "" }] } }));
  const removeIncentive = (i) => setForm((f) => ({ ...f, new_pad: { ...f.new_pad, incentives: f.new_pad.incentives.filter((_, idx) => idx !== i) } }));

  const pickCategory = (value) => {
    // Switching category rebuilds the date state from scratch: nothing prefilled for one
    // category may survive into another (a New PAD's lease start used to stick to whatever
    // was picked next). Only two categories prefill at all: a New PAD starts at the lease
    // start, a PAD Stop at the lease end. Every other date is typed by hand — including a
    // New Rent Amount's, whose addendum still supplies the amount but no longer the date.
    const blank = emptyForm(form.tenant_names);
    const patch = {
      category: value,
      effective_date: "",
      notes: "",
      new_pad: blank.new_pad,
      transfer: blank.transfer,
      amount_change: blank.amount_change,
      delay: blank.delay,
      split: blank.split,
      temp_stop: blank.temp_stop,
      stop: blank.stop,
    };
    // A New PAD is usually for the incoming resident; the row may still show the departing one.
    if (value === "NEW" && !existing && incoming?.tenants && (!form.tenant_names || form.tenant_names === tenantNames)) patch.tenant_names = incoming.tenants;
    if (value !== "NEW" && !existing && form.tenant_names === incoming?.tenants) patch.tenant_names = tenantNames || "";
    if (value === "NEW" && systemLeaseStart) patch.effective_date = systemLeaseStart;
    if (value === "STOP" && systemMoveOut) {
      patch.effective_date = systemMoveOut;
    }
    // Only a New PAD and a PAD Change carry paperwork; anything staged for one of those
    // must not ride silently into a category that will never show the panel again.
    if (value !== "NEW" && value !== "CHANGE") {
      setStagedFiles([]);
      setRemovedAttachmentIds([]);
    }
    set(patch);
    if (error) setError("");
  };

  // Only a New PAD and a PAD Change carry paperwork here (a Split PAD deliberately does not
  // — owner, 2026-08-29). The other categories hide the panel entirely — files already saved
  // on an old entry stay on the record and keep showing in the table and the report, just
  // not in this modal.
  const wantsAttachments = form.category === "NEW" || form.category === "CHANGE";
  const requiresAttachment = form.category === "NEW" || form.category === "CHANGE";

  // Documents still on the entry after the pending removals apply.
  const keptSavedCount = (existing?.attachments || []).filter(
    (a) => a && !a.removed && !removedAttachmentIds.includes(a.attachment_id)
  ).length;

  const validate = () => {
    if (!form.category) return "Pick what kind of PAD update this is.";
    // A split's date is its first pull — there is no separate date field to fill.
    if (form.category !== "SPLIT" && !form.effective_date) return "The effective date is required.";
    // Every date must be today or later on a new entry; SPLIT is covered pull by pull below.
    if (blockPast && form.effective_date && form.effective_date < today) {
      return `${(meta && meta.dateLabel) || "The date"} cannot be in the past.`;
    }
    if (form.category === "NEW" && firstPullEdited && !form.new_pad.reason.trim()) {
      return `The first pull differs from the lease start (${systemLeaseStart}) — give the reason, e.g. incentive for first month.`;
    }
    if (form.category === "NEW") {
      const seen = new Set();
      for (let i = 0; i < form.new_pad.incentives.length; i++) {
        const inc = form.new_pad.incentives[i];
        const amt = parseCharge(inc.amount);
        if (amt === null || amt <= 0) return `Enter the amount for incentive ${i + 1} — dollars above zero.`;
        if (totalRent !== null && amt > totalRent) return `Incentive ${i + 1} is more than the ${money(totalRent)} rent.`;
        if (!inc.date) return `Pick the date for incentive ${i + 1} — the day the reduced amount is pulled.`;
        if (blockPast && inc.date < today) return `Incentive ${i + 1}'s date cannot be in the past.`;
        const m = monthOf(inc.date);
        if (monthOf(form.effective_date) && m < monthOf(form.effective_date)) return `Incentive ${i + 1} is for a month before the first pull.`;
        if (seen.has(m)) return "Only one incentive per month — combine them into one amount.";
        seen.add(m);
      }
      if (form.new_pad.incentives.length && !form.new_pad.reason.trim()) return "Give the incentive reason — why the deal exists.";
    }
    if (form.category === "AMOUNT_CHANGE") {
      const amt = parseCharge(form.amount_change.one_time_amount);
      if (amt !== null && amt > 0 && !form.amount_change.one_time_reason.trim()) return "Give the reason for the one-time charge.";
      // A bare reason stands on its own (owner, 2026-08-31) — no amount required.
    }
    if (form.category === "DELAY" && parseCharge(form.delay.charge) === null) return "Enter the delay charge in dollars — 0 if there is none.";
    if (form.category === "DELAY" && delayNeedsApproval && !form.delay.approved_by.trim()) {
      return `This delay is past the ${dayOrdinal(delayApprovalDay)} of the month — who approved it? Enter the approver's name.`;
    }
    if (form.category === "DELAY" && !form.delay.na && !form.delay.resume_date) return "When does the PAD continue regularly? Pick a date, or N/A if it stays on the delayed date.";
    if (form.category === "DELAY" && form.delay.resume_date && form.delay.resume_date < form.effective_date) return "The PAD cannot resume before the pause starts.";
    if (form.category === "DELAY" && blockPast && form.delay.resume_date && form.delay.resume_date < today) return "The PAD cannot resume in the past.";
    if (form.category === "SPLIT") {
      for (let i = 0; i < form.split.pulls.length; i++) {
        const p = form.split.pulls[i];
        const amt = parseCharge(p.amount);
        if (amt === null || amt <= 0) return `Enter the amount for pull ${i + 1} — dollars above zero.`;
        if (!p.date) return `Pick the date for pull ${i + 1}.`;
        if (blockPast && p.date < today) return `Pull ${i + 1}'s date cannot be in the past.`;
      }
      const dates = form.split.pulls.map((p) => p.date);
      if (new Set(dates).size !== dates.length) return "Each split pull needs its own date.";
      if (!form.split.na && !form.split.resume_date) return "When does the PAD continue regularly? Pick a date, or N/A if the split repeats for the upcoming months.";
      if (form.split.resume_date && splitFirstDate && form.split.resume_date < splitFirstDate) return "The PAD cannot resume before the first split pull.";
      if (blockPast && form.split.resume_date && form.split.resume_date < today) return "The PAD cannot resume in the past.";
      if (totalRent === null) return "This unit has no rent on the mastersheet — a split cannot be validated.";
      if (!splitMatchesRent) {
        return splitCharge > 0
          ? `The pulls add up to ${money(splitTotal)} but the month's total is ${money(splitTarget)} (${money(totalRent)} rent + ${money(splitCharge)} extra charges) — they must match exactly.`
          : `The pulls add up to ${money(splitTotal)} but the total rent is ${money(totalRent)} — they must match exactly.`;
      }
      // The arrangement carries its approver — usually someone senior in the company.
      if (!form.split.approved_by.trim()) return "Who approved this split? Enter the approver's name.";
    }
    if (form.category === "TEMP_STOP" && !form.temp_stop.na && !form.temp_stop.resume_date) return "When does the PAD resume? Pick a date, or N/A if it stays stopped until the next notice.";
    if (form.category === "TEMP_STOP" && form.temp_stop.resume_date && form.temp_stop.resume_date < form.effective_date) return "The PAD cannot resume before the pause starts.";
    if (form.category === "TEMP_STOP" && blockPast && form.temp_stop.resume_date && form.temp_stop.resume_date < today) return "The PAD cannot resume in the past.";
    if (form.category === "TEMP_STOP" && !form.temp_stop.alternate_method) return "How is the resident paying meanwhile?";
    if (form.category === "STOP" && !form.stop.reason) return "Please pick a reason for stopping the PAD.";
    // A New PAD and a PAD Change need their paperwork: the email, notice or signed form
    // behind the change. The other categories carry no documents here at all.
    if (requiresAttachment && keptSavedCount + stagedFiles.length === 0) return "Attach at least one document: the paperwork behind this change.";
    return "";
  };

  const save = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }

    // One last look before it becomes part of the unit's PAD record: everything the entry
    // will say, in one card, so a wrong date or resident is caught here and not by accounting.
    const catMeta = categoryMeta(form.category);
    // A split's pulls travel sorted by date: the first one IS the entry's effective date.
    const sortedPulls =
      form.category === "SPLIT"
        ? form.split.pulls.map((p) => ({ date: p.date, amount: parseCharge(p.amount) || 0 })).sort((a, b) => a.date.localeCompare(b.date))
        : [];
    const summaryRows = [
      ["Unit", [unitNo ? `Unit ${unitNo}` : "", buildingName, propertyName].filter(Boolean).join(" · ")],
      ["Resident", form.tenant_names || tenantNames || "—"],
      [catMeta.dateLabel || "Effective", form.category === "SPLIT" ? splitFirstDate : form.effective_date],
      ...(form.category === "NEW"
        ? [
            ...(firstPullEdited || form.new_pad.incentives.length ? [["Incentive reason", form.new_pad.reason.trim() || "—"]] : []),
            ...form.new_pad.incentives.map((inc, i) => {
              const amt = parseCharge(inc.amount) || 0;
              return [
                `Incentive ${i + 1}`,
                `${money(amt)} off ${formatMonthLabel(monthOf(inc.date))} → charge ${totalRent === null ? "—" : money(round2(totalRent - amt))} on ${inc.date}`,
              ];
            }),
          ]
        : []),
      ...(form.category === "TRANSFER" ? [["Reason", form.transfer.reason.trim() || "—"]] : []),
      ...(form.category === "DELAY" ? [
        ["Delay charge", money(parseCharge(form.delay.charge) || 0)],
        ...(totalRent !== null && parseCharge(form.delay.charge) > 0 ? [["Total to charge", `${money(totalRent + parseCharge(form.delay.charge))} (${money(totalRent)} rent + ${money(parseCharge(form.delay.charge))} delay charge)`]] : []),
        ...(delayNeedsApproval ? [["Approved by", form.delay.approved_by.trim() || "—"]] : []),
        ...(parseCharge(form.delay.charge) > 0 || form.delay.late_fees_collected
          ? [["Late fees", form.delay.late_fees_collected ? "Collected" : "Not collected yet"]]
          : []),
        ["Resumes", form.delay.na ? "Same delayed date for the upcoming months" : form.delay.resume_date],
      ] : []),
      ...(form.category === "SPLIT" ? [
        ...sortedPulls.map((p, i) => [`Pull ${i + 1}`, `${money(p.amount)} on ${p.date}`]),
        ...(splitCharge > 0 ? [["Extra charges", `${money(splitCharge)} (${money(totalRent)} rent + ${money(splitCharge)} = ${money(splitTarget)})`]] : []),
        ["Total", `${money(splitTotal)} — matches the month's total`],
        ["Approved by", form.split.approved_by.trim() || "—"],
        ["Resumes", form.split.na ? "Split repeats every month, until replaced" : `Regular pulls resume ${form.split.resume_date}`],
      ] : []),
      ...(form.category === "TEMP_STOP" ? [
        ["Resumes", form.temp_stop.na ? "Stopped until the next notice" : form.temp_stop.resume_date],
        ["Paying by", form.temp_stop.alternate_method],
      ] : []),
      ...(form.category === "STOP" ? [
        ["Reason", (STOP_REASONS.find((r) => r.value === form.stop.reason) || {}).label || form.stop.reason],
      ] : []),
      ...(form.category === "AMOUNT_CHANGE" && oneTimeCharge > 0
        ? [
            ["One-time charge", `${money(oneTimeCharge)} — ${form.amount_change.one_time_reason.trim()}`],
            ...(totalRent !== null ? [["Total to charge", `${money(totalRent + oneTimeCharge)} this month · back to ${money(totalRent)} next month`]] : []),
          ]
        : []),
      // A bare reason (no one-time charge) is still part of the record — show it.
      ...(form.category === "AMOUNT_CHANGE" && oneTimeCharge <= 0 && form.amount_change.one_time_reason.trim()
        ? [["Reason", form.amount_change.one_time_reason.trim()]]
        : []),
      ...(wantsAttachments ? [["Attachments", String(keptSavedCount + stagedFiles.length)]] : []),
      ...(form.notes ? [["Notes", form.notes]] : []),
    ];
    const confirmed = await Swal.fire({
      title: "Save this PAD update?",
      html:
        `<div style="text-align:left;font-size:0.92rem;">` +
        `<p style="color:#64748B;margin:0 0 12px;">Please confirm everything below is correct — this becomes part of the unit's PAD record.</p>` +
        `<div style="display:inline-flex;align-items:center;gap:6px;background:${catMeta.bg};color:${catMeta.color};font-weight:700;border-radius:999px;padding:4px 12px;margin-bottom:10px;">${catMeta.label}</div>` +
        `<table style="width:100%;border-collapse:collapse;">` +
        summaryRows.map(([k, v]) =>
          `<tr><td style="color:#64748B;padding:3px 12px 3px 0;white-space:nowrap;vertical-align:top;">${k}</td><td style="font-weight:600;overflow-wrap:anywhere;">${escapeText(v)}</td></tr>`
        ).join("") +
        `</table></div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, save it",
      cancelButtonText: "Go back",
      confirmButtonColor: "#0C447C",
      reverseButtons: true,
    });
    if (!confirmed.isConfirmed) return;

    setSaving(true);
    setError("");
    const payload = {
      // An archived tenancy's entry is keyed by the archived record; the server anchors
      // it to the permanent unit row and flags it archived from birth.
      ...(archivedMode ? { archived_item_id: archivedItemId } : { mastersheet_item_id: itemId }),
      category: form.category,
      // A split starts when its first pull does; every other category types its date.
      effective_date: form.category === "SPLIT" ? splitFirstDate : form.effective_date,
      // The bank always pulls on the 1st; a Delay moves that month's pull to the chosen day,
      // and a Split's day is its first pull's.
      pull_day:
        form.category === "DELAY"
          ? Number(form.effective_date.slice(8, 10)) || 1
          : form.category === "SPLIT"
            ? Number(splitFirstDate.slice(8, 10)) || 1
            : 1,
      notes: form.notes,
      tenant_names: (form.tenant_names || tenantNames || "").trim(),
    };
    // The lease start it was pre-filled from rides along, so the record says what the first
    // pull was moved FROM. The reason travels as typed — it is the Incentive Reason too.
    if (form.category === "NEW") payload.new_pad = {
      lease_start_date: systemLeaseStart || "",
      reason: form.new_pad.reason.trim(),
      incentives: form.new_pad.incentives
        .map((inc) => ({ date: inc.date, amount: parseCharge(inc.amount) || 0 }))
        .sort((a, b) => a.date.localeCompare(b.date)),
    };
    // A Transfer PAD is a New PAD without paperwork: no attachments, and the reason —
    // when one was given — travels with the entry and shows on the report.
    if (form.category === "TRANSFER") payload.transfer = { reason: form.transfer.reason.trim() };
    // N/A is stored as an empty resume date: the delayed date holds for the upcoming months.
    if (form.category === "DELAY") payload.delay = {
      month: monthOf(form.effective_date),
      resume_date: form.delay.na ? "" : form.delay.resume_date,
      charge: parseCharge(form.delay.charge),
      // The approver travels only when the date needs one — a name typed before the date
      // moved back under the cutoff must not ride along silently.
      approved_by: delayNeedsApproval ? form.delay.approved_by.trim() : "",
      late_fees_collected: !!form.delay.late_fees_collected,
    };
    // The split's schedule: validated above to add up to the mastersheet rent exactly; the
    // server re-checks against the same number before it saves. N/A is stored as an empty
    // resume date: the split repeats for the upcoming months until another entry replaces it.
    if (form.category === "SPLIT") {
      payload.split = {
        pulls: sortedPulls,
        approved_by: form.split.approved_by.trim(),
        resume_date: form.split.na ? "" : form.split.resume_date,
        charge: splitCharge,
      };
    }
    if (form.category === "TEMP_STOP") payload.temp_stop = { alternate_method: form.temp_stop.alternate_method, resume_date: form.temp_stop.na ? "" : form.temp_stop.resume_date };
    if (form.category === "STOP") payload.stop = form.stop;
    // The one-time charge is the entry's own dollars; the rent itself stays derived from
    // the mastersheet on the server, so it is never sent.
    if (form.category === "AMOUNT_CHANGE") payload.amount_change = {
      one_time_amount: parseCharge(form.amount_change.one_time_amount) || 0,
      one_time_reason: form.amount_change.one_time_reason.trim(),
    };
    try {
      // 1. The entry itself: a new one has to exist before anything can hang off it.
      setSavingStep(stagedFiles.length || removedAttachmentIds.length ? "Saving entry…" : "");
      let entryId = existing?._id || createdId;
      if (entryId) {
        await updatePadEntry(entryId, payload);
      } else {
        const res = await createPadEntry(payload);
        entryId = res?.data?.data?._id;
        if (!entryId) {
          if (stagedFiles.length) throw new Error("The entry saved but did not return an id, so the files were not attached.");
        } else {
          setCreatedId(entryId);
        }
      }

      // 2. Removals before uploads, so swapping a file for a better one cannot trip the
      //    ten-attachment ceiling.
      for (const attachmentId of [...removedAttachmentIds]) {
        setSavingStep("Removing attachments…");
        await removePadAttachment(entryId, attachmentId);
        // Drop it as it lands: a retry after a later failure must not repeat finished work.
        setRemovedAttachmentIds((prev) => prev.filter((x) => x !== attachmentId));
      }

      // 3. Upload each staged file, then record it against the entry.
      const toUpload = [...stagedFiles];
      for (let i = 0; i < toUpload.length; i++) {
        const file = toUpload[i];
        setSavingStep(`Uploading ${i + 1} of ${toUpload.length}…`);
        const uploaded = await uploadPadAttachmentFile(file);
        await addPadAttachment(entryId, {
          key: uploaded.key,
          filename: file.name,
          size: file.size,
          mime: file.type,
        });
        setStagedFiles((prev) => prev.filter((f) => f !== file));
      }

      const n = toUpload.length;
      toast.success(
        `${existing?._id ? "PAD info updated" : "PAD info recorded"}${n ? ` · ${n} file${n === 1 ? "" : "s"} attached` : ""}`
      );
      onSaved && onSaved();
      toggle();
    } catch (e) {
      const base = e?.response?.data?.message || e.message || "Failed to save PAD info";
      // Say plainly that the entry is already saved, or someone will re-type the whole thing.
      const msg = createdId || existing?._id ? `${base} The entry itself is saved. Press Save again to finish the attachments.` : base;
      setError(msg);
      toast.error(base);
    } finally {
      setSaving(false);
      setSavingStep("");
    }
  };

  return (
    // backdrop="static" + keyboard off: half-filled PAD info must not vanish on a stray
    // click beside the dialog — only Cancel or the header's × closes it (owner, 2026-08-31).
    <Modal isOpen={isOpen} toggle={toggle} backdrop="static" keyboard={false} size="lg" centered className="pad-info-modal-dialog" contentClassName="pad-info-modal-content">
      <style>{`
        /* A bit wider than lg so the two-column top row breathes; still fits a 13" laptop. */
        .pad-info-modal-dialog { max-width: 860px; }
        .pad-info-modal-content { border: none; border-radius: 14px; overflow: hidden; box-shadow: 0 24px 70px rgba(12,68,124,0.20); }
        .pad-info-modal-content input::placeholder, .pad-info-modal-content textarea::placeholder { color: #9AA7B8; }
        .pad-info-modal-content select:focus, .pad-info-modal-content input:focus, .pad-info-modal-content textarea:focus { outline: none; }
        .pim-focusable:focus-within { border-color: ${C.primaryMd}; box-shadow: 0 0 0 2px rgba(26,111,181,0.12); }

        /* Category list on the left, attachments on the right; the right panel stretches to
           the list's height so the two columns always share one bottom edge. */
        .pim-toprow { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr); gap: 16px; align-items: stretch; }
        .pim-col { display: flex; flex-direction: column; min-width: 0; }
        .pim-col > :last-child { flex: 1; min-height: 0; }
        @media (max-width: 640px) { .pim-toprow { grid-template-columns: 1fr; } }

        /* Wider tracks so a category's fields stretch across the full dialog instead of
           huddling in narrow columns beside empty space (owner, 2026-08-31). auto-fit
           collapses unused tracks, so two fields split the whole row between them. */
        .pim-fields { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 14px; margin-top: 16px; }
        .pim-btn { border: none; cursor: pointer; font-size: 13px; font-weight: 600; border-radius: 8px;
          padding: 9px 16px; display: inline-flex; align-items: center; gap: 7px; font-family: inherit; }
        .pim-btn-primary { background: ${C.primary}; color: #fff; box-shadow: 0 6px 18px rgba(12,68,124,0.28); }
        .pim-btn-primary:hover:not(:disabled) { background: #083460; }
        .pim-btn-primary:disabled { background: #94A3B8; box-shadow: none; cursor: not-allowed; }
        .pim-btn-ghost { background: #fff; color: ${C.text}; border: 1px solid ${C.inputBorder}; }
        .pim-btn-ghost:hover { background: #F1F5FA; }
        .pim-iconbtn { background: none; border: none; cursor: pointer; color: ${C.muted}; display: inline-flex; }
        .pim-iconbtn:hover { color: ${C.text}; }
        .pim-select { border: none; outline: none; background: transparent; flex: 1; width: 100%; font-size: ${T.body}px; color: ${C.text}; height: 100%; font-family: inherit; }

        /* The small N/A pill beside a resume-date label. */
        .pim-na { border: 1px solid ${C.inputBorder}; background: #fff; color: ${C.muted}; font-size: 10px; font-weight: 700;
          letter-spacing: .05em; border-radius: 999px; padding: 1px 9px; line-height: 1.6; cursor: pointer;
          font-family: inherit; flex: none; transition: background .12s, color .12s, border-color .12s; }
        .pim-na:hover:not(:disabled) { border-color: ${C.primaryMd}; color: ${C.primaryMd}; }
        .pim-na.on { background: ${C.primaryMd}; border-color: ${C.primaryMd}; color: #fff; }
        .pim-na:disabled { cursor: not-allowed; opacity: .6; }
      `}</style>

      {/* ---- header: what this is, and who/where. The resident lives here, nowhere else ---- */}
      <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "12px 18px", borderBottom: `1px solid ${C.border}`, background: "#fff" }}>
        <span style={{ width: 34, height: 34, borderRadius: 9, background: "#EBF3FC", color: C.primary, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
          <FeatherIcon icon="credit-card" size={17} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: T.title, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>
            {existing?._id ? "Edit PAD info" : "Record PAD info"}
          </div>
          <div style={{ fontSize: T.hint, color: C.muted, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {[unitNo ? `Unit ${unitNo}` : "", buildingName, propertyName, tenantNames, archivedMode ? "Archived tenancy" : ""].filter(Boolean).join(" · ")}
          </div>
        </div>
        <button type="button" className="pim-iconbtn" onClick={toggle} aria-label="Close">
          <FeatherIcon icon="x" size={18} />
        </button>
      </div>

      <div style={styles.body}>
        {/* ---- category, with attachments beside it when this category carries paperwork ---- */}
        <div className={wantsAttachments ? "pim-toprow" : ""}>
          <div className="pim-col">
            <label id="pim-cat-label" style={styles.label}>{archivedMode ? "PAD update for this archived tenancy" : "What kind of PAD update is this?"}</label>
            <CategoryPicker
              value={form.category}
              onPick={pickCategory}
              disabled={saving}
              labelledBy="pim-cat-label"
              categories={archivedMode ? PAD_CATEGORIES.filter((c) => c.value === "STOP") : PAD_CATEGORIES}
            />
          </div>
          {wantsAttachments && (
            <div className="pim-col">
              <label style={styles.label}>Attachments</label>
              <PadAttachments
                fill
                entryId={existing?._id || createdId}
                saved={existing?.attachments || []}
                staged={stagedFiles}
                removedIds={removedAttachmentIds}
                onStage={(files) => {
                  setStagedFiles((prev) => [...prev, ...files]);
                  if (error) setError("");
                }}
                onUnstage={(i) => setStagedFiles((prev) => prev.filter((_, idx) => idx !== i))}
                onToggleRemove={(id) =>
                  setRemovedAttachmentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
                }
                unitNo={unitNo}
                propertyName={propertyName}
                entryCategoryLabel={meta?.label}
                entryDate={form.effective_date}
                canWrite
                busy={saving}
              />
            </div>
          )}
        </div>

        {/* ---- the fields this category needs ---- */}
        {meta && (
          <div className="pim-fields">
            {/* A split has no date of its own — its first pull is the date (set on save). */}
            {form.category !== "SPLIT" && (
              <Field label={meta.dateLabel || "Effective from"}>
                <Box title={form.category === "NEW" && systemLeaseStart ? `Lease starts ${systemLeaseStart}` : undefined}>
                  <FeatherIcon icon="calendar" size={14} color={C.muted} />
                  <input
                    type="date"
                    style={styles.bareInput}
                    // The picker fences the past off on a new entry; a typed past date is
                    // caught by validate() with the same rule.
                    min={blockPast ? today : undefined}
                    value={form.effective_date}
                    onChange={(e) => set({ effective_date: e.target.value })}
                  />
                </Box>
              </Field>
            )}

            {/* The Incentive Reason: one why for the whole deal, and still the why when the
                first pull was moved off the lease start. Always visible on a New PAD. */}
            {form.category === "NEW" && (
              <Field label={firstPullEdited ? `Incentive reason (lease starts ${systemLeaseStart})` : "Incentive reason"}>
                <Box>
                  <FeatherIcon icon="edit-3" size={14} color={C.muted} />
                  <input
                    type="text"
                    style={styles.bareInput}
                    placeholder="e.g. First month free promotion"
                    maxLength={500}
                    value={form.new_pad.reason}
                    onChange={(e) => setSub("new_pad", { reason: e.target.value })}
                  />
                </Box>
              </Field>
            )}

            {/* The move-in deal: months whose rent is discounted — amount off + the day the
                reduced pull is made (its month IS the discounted month). First month and
                last month are the usual suspects; any month at or after the first pull works. */}
            {fields.includes("incentives") && (
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <label style={{ ...styles.label, marginBottom: 0 }}>Incentives</label>
                  <span style={styles.hint}>each row discounts one month — the date is the day the reduced amount is pulled</span>
                </div>
                {form.new_pad.incentives.map((inc, i) => {
                  const amt = parseCharge(inc.amount);
                  const m = monthOf(inc.date);
                  return (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                      <span style={{ ...styles.hint, width: 16, flex: "none", fontWeight: 700, textAlign: "right" }}>{i + 1}</span>
                      <Box style={{ flex: "1 1 200px" }}>
                        <FeatherIcon icon="dollar-sign" size={14} color={C.muted} />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Total amount of incentive given off"
                          aria-label={`Incentive ${i + 1} amount`}
                          style={styles.bareInput}
                          value={inc.amount}
                          onChange={(e) => setIncentive(i, { amount: e.target.value })}
                        />
                      </Box>
                      <Box style={{ flex: "1 1 200px" }}>
                        <FeatherIcon icon="calendar" size={14} color={C.muted} />
                        <input
                          type="date"
                          aria-label={`Incentive ${i + 1} date`}
                          style={styles.bareInput}
                          min={blockPast ? today : undefined}
                          value={inc.date}
                          onChange={(e) => setIncentive(i, { date: e.target.value })}
                        />
                      </Box>
                      <button type="button" className="pim-iconbtn" aria-label={`Remove incentive ${i + 1}`} onClick={() => removeIncentive(i)} disabled={saving}>
                        <FeatherIcon icon="trash-2" size={15} />
                      </button>
                      {/* What this row means in dollars, the moment it is complete. */}
                      {amt !== null && amt > 0 && m && totalRent !== null && (
                        <span style={{ ...styles.hint, fontWeight: 700, flexBasis: "100%", paddingLeft: 26, color: amt <= totalRent ? "#0F6E56" : "#A32D2D" }}>
                          {amt <= totalRent
                            ? `charge ${money(round2(totalRent - amt))} for ${formatMonthLabel(m)} (${money(totalRent)} rent − ${money(amt)} incentive)`
                            : `the incentive is more than the ${money(totalRent)} rent`}
                        </span>
                      )}
                    </div>
                  );
                })}
                <button type="button" className="pim-btn pim-btn-ghost" onClick={addIncentive} disabled={saving}>
                  <FeatherIcon icon="plus" size={13} /> {form.new_pad.incentives.length ? "Add another incentive" : "Add incentive"}
                </button>
              </div>
            )}

            {/* The optional one-time extra: dollars this month collects on top of the rent,
                with its why. Next month the plain rent returns on its own. */}
            {fields.includes("one_time") && (
              <Field label="One-time charge ($)">
                <Box>
                  <FeatherIcon icon="dollar-sign" size={14} color={C.muted} />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    aria-label="One-time charge amount"
                    style={styles.bareInput}
                    value={form.amount_change.one_time_amount}
                    onChange={(e) => setSub("amount_change", { one_time_amount: e.target.value })}
                  />
                </Box>
              </Field>
            )}
            {fields.includes("one_time") && (
              <Field label="Reason">
                <Box>
                  <FeatherIcon icon="edit-3" size={14} color={C.muted} />
                  <input
                    type="text"
                    style={styles.bareInput}
                    placeholder="e.g. Carpet cleaning"
                    aria-label="One-time charge reason"
                    maxLength={500}
                    value={form.amount_change.one_time_reason}
                    onChange={(e) => setSub("amount_change", { one_time_reason: e.target.value })}
                  />
                </Box>
              </Field>
            )}

            {/* A Transfer PAD's why — optional, but shown on the report wherever it was given. */}
            {fields.includes("transfer_reason") && (
              <Field label="Reason">
                <Box>
                  <FeatherIcon icon="edit-3" size={14} color={C.muted} />
                  <input
                    type="text"
                    style={styles.bareInput}
                    placeholder="e.g. Moved from another unit"
                    maxLength={500}
                    value={form.transfer.reason}
                    onChange={(e) => setSub("transfer", { reason: e.target.value })}
                  />
                </Box>
              </Field>
            )}

            {fields.includes("delay_resume") && (
              <Field
                label="PAD continues regularly on"
                labelExtra={
                  <NaToggle
                    on={form.delay.na}
                    disabled={saving}
                    onClick={() => setSub("delay", { na: !form.delay.na, resume_date: form.delay.na ? form.delay.resume_date : "" })}
                  />
                }
              >
                {form.delay.na ? (
                  <Box style={{ background: C.soft }} title="Select N/A when the same delayed date holds for all the upcoming months">
                    <FeatherIcon icon="repeat" size={14} color={C.muted} />
                    <span style={{ ...styles.bareInput, display: "flex", alignItems: "center", color: C.muted }}>
                      Same delayed date for the upcoming months
                    </span>
                  </Box>
                ) : (
                  <Box>
                    <FeatherIcon icon="play-circle" size={14} color={C.muted} />
                    <input type="date" style={styles.bareInput} min={resumeMin} value={form.delay.resume_date} onChange={(e) => setSub("delay", { resume_date: e.target.value })} />
                  </Box>
                )}
              </Field>
            )}

            {/* The delay surcharge: the one dollar figure an entry records, added to each
                delayed month's pull. Cents welcome, zero allowed — but it must be typed. */}
            {fields.includes("delay_resume") && (
              <Field label="Delay charge ($)">
                <Box>
                  <FeatherIcon icon="dollar-sign" size={14} color={C.muted} />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    style={styles.bareInput}
                    value={form.delay.charge}
                    onChange={(e) => setSub("delay", { charge: e.target.value })}
                  />
                </Box>
              </Field>
            )}

            {/* Past the company's cutoff day the delay carries its approver — the same
                rule as a split: an arrangement someone senior signed off on. */}
            {fields.includes("delay_resume") && delayNeedsApproval && (
              <Field label={`Approved by (delay past the ${dayOrdinal(delayApprovalDay)})`}>
                <Box>
                  <FeatherIcon icon="user-check" size={14} color={C.muted} />
                  <input
                    type="text"
                    style={styles.bareInput}
                    placeholder="e.g. Name of the approving manager"
                    aria-label="Delay approved by"
                    maxLength={200}
                    value={form.delay.approved_by}
                    onChange={(e) => setSub("delay", { approved_by: e.target.value })}
                  />
                </Box>
              </Field>
            )}

            {/* Ticked once the delay's late fee has actually been collected — now, or later
                by reopening the entry. */}
            {fields.includes("delay_resume") && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, gridColumn: "1 / -1" }}>
                <label style={{ ...styles.label, marginBottom: 0, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={!!form.delay.late_fees_collected}
                    disabled={saving}
                    onChange={(e) => setSub("delay", { late_fees_collected: e.target.checked })}
                  />
                  Late fees collected
                </label>
                <span style={styles.hint}>tick once the fee has actually been collected</span>
              </div>
            )}

            {/* The split schedule: the rent pulled in parts — a repeater of amount + date
                rows, at least two, that must add up to the mastersheet rent exactly. */}
            {fields.includes("split_pulls") && (
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <label style={{ ...styles.label, marginBottom: 0 }}>Split pulls</label>
                  <span style={styles.hint}>repeat every month on the same days, until replaced</span>
                </div>
                {form.split.pulls.map((p, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
                    <span style={{ ...styles.hint, width: 16, flex: "none", fontWeight: 700, textAlign: "right" }}>{i + 1}</span>
                    <Box style={{ flex: 1 }}>
                      <FeatherIcon icon="dollar-sign" size={14} color={C.muted} />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        aria-label={`Pull ${i + 1} amount`}
                        style={styles.bareInput}
                        value={p.amount}
                        onChange={(e) => setPull(i, { amount: e.target.value })}
                      />
                    </Box>
                    <Box style={{ flex: 1 }}>
                      <FeatherIcon icon="calendar" size={14} color={C.muted} />
                      <input
                        type="date"
                        aria-label={`Pull ${i + 1} date`}
                        style={styles.bareInput}
                        min={blockPast ? today : undefined}
                        value={p.date}
                        onChange={(e) => setPull(i, { date: e.target.value })}
                      />
                    </Box>
                    {/* Two pulls are the floor — fewer is not a split. */}
                    <button
                      type="button"
                      className="pim-iconbtn"
                      aria-label={`Remove pull ${i + 1}`}
                      onClick={() => removePull(i)}
                      disabled={saving || form.split.pulls.length <= 2}
                      style={form.split.pulls.length <= 2 ? { opacity: 0.35, cursor: "not-allowed" } : undefined}
                    >
                      <FeatherIcon icon="trash-2" size={15} />
                    </button>
                  </div>
                ))}
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <button type="button" className="pim-btn pim-btn-ghost" onClick={addPull} disabled={saving}>
                    <FeatherIcon icon="plus" size={13} /> Add another pull
                  </button>
                  {/* The running tally, in plain words: the month's total, what the splits
                      cover so far, and exactly what is left to add (or take back out). */}
                  {splitTarget !== null && (
                    <span style={{ ...styles.hint, fontWeight: 700, color: splitMatchesRent ? "#0F6E56" : "#A32D2D" }}>
                      {(() => {
                        const n = form.split.pulls.length;
                        const word = splitCharge > 0 ? "Total with extra charges" : "Total rent";
                        if (splitMatchesRent) return `All set — your ${n} splits cover the ${money(splitTarget)} total exactly`;
                        if (splitTotal < splitTarget)
                          return `${word} ${money(splitTarget)} · you have created ${n} splits covering ${money(splitTotal)} · ${money(splitTarget - splitTotal)} remaining to be added`;
                        return `${word} ${money(splitTarget)} · you have created ${n} splits covering ${money(splitTotal)} · ${money(splitTotal - splitTarget)} too much — reduce a pull`;
                      })()}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Extra dollars the month collects on top of the rent — typed by the manager
                and folded into the total the splits must cover. Blank = none. */}
            {fields.includes("split_pulls") && (
              <Field label="Extra charges ($)">
                <Box>
                  <FeatherIcon icon="dollar-sign" size={14} color={C.muted} />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    aria-label="Extra charges for split pulls"
                    style={styles.bareInput}
                    value={form.split.charge}
                    onChange={(e) => setSub("split", { charge: e.target.value })}
                  />
                </Box>
              </Field>
            )}

            {/* When the regular single pull comes back — same control as the delay's.
                N/A means the split repeats every month until another entry replaces it;
                a date makes the resume month's report ask for the pull to be restarted. */}
            {fields.includes("split_pulls") && (
              <Field
                label="PAD continues regularly on"
                labelExtra={
                  <NaToggle
                    on={form.split.na}
                    disabled={saving}
                    onClick={() => setSub("split", { na: !form.split.na, resume_date: form.split.na ? form.split.resume_date : "" })}
                  />
                }
              >
                {form.split.na ? (
                  <Box style={{ background: C.soft }} title="Select N/A when the split keeps repeating for the upcoming months">
                    <FeatherIcon icon="repeat" size={14} color={C.muted} />
                    <span style={{ ...styles.bareInput, display: "flex", alignItems: "center", color: C.muted }}>
                      Split repeats every month, until replaced
                    </span>
                  </Box>
                ) : (
                  <Box>
                    <FeatherIcon icon="play-circle" size={14} color={C.muted} />
                    <input
                      type="date"
                      style={styles.bareInput}
                      min={splitResumeMin}
                      value={form.split.resume_date}
                      onChange={(e) => setSub("split", { resume_date: e.target.value })}
                    />
                  </Box>
                )}
              </Field>
            )}

            {/* Who signed off on the split arrangement — usually someone senior in the
                company. Required: a split does not save without its approver's name. */}
            {fields.includes("split_pulls") && (
              <Field label="Approved by">
                <Box>
                  <FeatherIcon icon="user-check" size={14} color={C.muted} />
                  <input
                    type="text"
                    style={styles.bareInput}
                    placeholder="e.g. Name of the approving manager"
                    maxLength={200}
                    value={form.split.approved_by}
                    onChange={(e) => setSub("split", { approved_by: e.target.value })}
                  />
                </Box>
              </Field>
            )}

            {fields.includes("temp_resume") && (
              <Field
                label="PAD resumes on"
                labelExtra={
                  <NaToggle
                    on={form.temp_stop.na}
                    disabled={saving}
                    onClick={() => setSub("temp_stop", { na: !form.temp_stop.na, resume_date: form.temp_stop.na ? form.temp_stop.resume_date : "" })}
                  />
                }
              >
                {form.temp_stop.na ? (
                  <Box style={{ background: C.soft }} title="Select N/A when the PAD stays stopped until the next notice">
                    <FeatherIcon icon="pause-circle" size={14} color={C.muted} />
                    <span style={{ ...styles.bareInput, display: "flex", alignItems: "center", color: C.muted }}>
                      Stopped until the next notice
                    </span>
                  </Box>
                ) : (
                  <Box>
                    <FeatherIcon icon="play-circle" size={14} color={C.muted} />
                    <input type="date" style={styles.bareInput} min={resumeMin} value={form.temp_stop.resume_date} onChange={(e) => setSub("temp_stop", { resume_date: e.target.value })} />
                  </Box>
                )}
              </Field>
            )}

            {fields.includes("temp_method") && (
              <Field label="Paying meanwhile by">
                <Box>
                  <select className="pim-select" aria-label="Paying meanwhile by" value={form.temp_stop.alternate_method} onChange={(e) => setSub("temp_stop", { alternate_method: e.target.value })}>
                    <option value="">Select…</option>
                    {ALTERNATE_METHODS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </Box>
              </Field>
            )}

            {fields.includes("stop_reason") && (
              <Field label="Reason">
                <Box>
                  <select className="pim-select" aria-label="Stop reason" value={form.stop.reason} onChange={(e) => setSub("stop", { reason: e.target.value })}>
                    <option value="">Select…</option>
                    {STOP_REASONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </Box>
              </Field>
            )}

            {/* Total rent: read-only, always the mastersheet's number — plus whatever charge
                the picked category adds this month. The breakdown is the tooltip, not a
                sentence under the field. A PAD Stop shows no rent at all — the resident is
                moving out, so the number is noise. */}
            {form.category !== "STOP" && (
            <Field
              label={
                delayCharge > 0
                  ? "Total rent (with delay charge)"
                  : form.category === "SPLIT"
                    ? "Total rent (to split)"
                    : oneTimeCharge > 0
                      ? "Total rent (with one-time charge)"
                      : "Total rent"
              }
            >
              <Box
                style={{ background: C.soft }}
                title={
                  delayCharge > 0 && totalRent !== null
                    ? `${money(totalRent)} rent + ${money(delayCharge)} delay charge`
                    : splitCharge > 0 && totalRent !== null
                      ? `${money(totalRent)} rent + ${money(splitCharge)} extra charges`
                      : oneTimeCharge > 0 && totalRent !== null
                        ? `${money(totalRent)} rent + ${money(oneTimeCharge)} one-time charge — returns to ${money(totalRent)} next month`
                        : rentBreakdown.map((r) => `${r.label} ${money(r.amount)}`).join(" + ")
                }
              >
                <FeatherIcon icon="dollar-sign" size={14} color={C.muted} />
                <span style={{ ...styles.bareInput, display: "flex", alignItems: "center", fontWeight: 700 }}>
                  {totalRent === null ? "—" : money(totalRent + delayCharge + splitCharge + oneTimeCharge)}
                </span>
                <FeatherIcon icon="lock" size={12} color={C.muted} />
              </Box>
            </Field>
            )}
          </div>
        )}

        {/* ---- notes ---- */}
        {meta && (
          <Field label="Notes for accounting" style={{ marginTop: 14 }}>
            <Box>
              <FeatherIcon icon="file-text" size={14} color={C.muted} />
              <input
                type="text"
                style={styles.bareInput}
                placeholder="e.g. Sending e-transfer for Jan"
                maxLength={4000}
                value={form.notes}
                onChange={(e) => set({ notes: e.target.value })}
              />
            </Box>
          </Field>
        )}
      </div>

      {/* ---- footer ---- */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 18px", borderTop: `1px solid ${C.border}`, background: "#fff" }}>
        <span style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 7 }}>
          {error ? (
            <>
              <FeatherIcon icon="alert-circle" size={14} color="#A32D2D" style={{ flex: "none" }} />
              <span style={{ ...styles.hint, color: "#A32D2D", fontWeight: 600 }}>{error}</span>
            </>
          ) : null}
        </span>
        <div style={{ display: "flex", gap: 8, flex: "none" }}>
          <button
            type="button"
            className="pim-btn pim-btn-ghost"
            onClick={() => {
              // A failed save may have already created the entry; refresh so the list is honest.
              if (createdId) onSaved && onSaved();
              toggle();
            }}
            disabled={saving}
          >
            {createdId ? "Close" : "Cancel"}
          </button>
          <button type="button" className="pim-btn pim-btn-primary" onClick={save} disabled={saving || !meta}>
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="save" size={14} />}
            {saving && savingStep ? savingStep : existing?._id ? "Save changes" : "Record PAD info"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PadInfoModal;
